import React, { useState, useEffect } from 'react';
import { Container, Table, Spinner, Alert, Button, Badge } from 'react-bootstrap';
import { getData } from '../services/api';
import { useNavigate } from 'react-router-dom';

const AppointmentsAdminPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getData();
        const appts = data
          .filter((item) => item.type === 'appointment')
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setAppointments(appts);
      } catch (err) {
        console.error('Error fetching appointments:', err);
        setError('Failed to load appointments. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('isAuthenticated');
    navigate('/login');
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return value;
  };

  // Each appointment type gets its own badge color
  const typeVariant = (type) => {
    switch (type) {
      case 'Pastor Appointment':
        return 'primary'; // blue
      case 'Saturday Teaching':
        return 'success'; // green
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">
          Appointment Requests{' '}
          <Badge bg="primary" pill>
            {appointments.length}
          </Badge>
        </h1>
        <div>
          <Button variant="outline-secondary" className="me-2" onClick={() => navigate('/admin')}>
            <i className="bi bi-people"></i> Registrations
          </Button>
          <Button variant="outline-danger" onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </div>

      {appointments.length === 0 ? (
        <Alert variant="info">No appointment requests found.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Preferred Date</th>
              <th>Preferred Time</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Message</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {appointments.map((appt) => (
              <tr key={appt.id}>
                <td>
                  {appt.firstName} {appt.lastName}
                </td>
                <td>
                  {appt.appointmentType ? (
                    <Badge bg={typeVariant(appt.appointmentType)}>
                      {appt.appointmentType}
                    </Badge>
                  ) : (
                    '-'
                  )}
                </td>
                <td>{formatDate(appt.preferredDate)}</td>
                <td>{formatDate(appt.preferredTime)}</td>
                <td>
                  {appt.phone ? (
                    <a href={`tel:${appt.phone}`} className="text-decoration-none">
                      {appt.phone}
                    </a>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  {appt.email ? (
                    <a href={`mailto:${appt.email}`} className="text-decoration-none">
                      {appt.email}
                    </a>
                  ) : (
                    '-'
                  )}
                </td>
                <td style={{ maxWidth: 240, whiteSpace: 'pre-wrap' }}>{appt.message || '-'}</td>
                <td>{appt.timestamp ? new Date(appt.timestamp).toLocaleString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default AppointmentsAdminPage;
